import {Alert, Container, IconButton, InputAdornment, Stack, TextField, Typography} from "@mui/material"
import AccountCircle from '@mui/icons-material/AccountCircle';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import Visibility from '@mui/icons-material/Visibility';
import {LoadingButton} from "@mui/lab";
import {createRef, useEffect, useState} from 'react';
import {NavLink, useNavigate} from "react-router-dom";
import {useTranslation} from 'i18nano';
import {ApiService} from "../api/ApiService";
import {useAuthStore} from "../stores/authStore";
import {useUserStore} from "../stores/userStore";
import {CheckNeedAdditionalAuth} from "../helpers/authHelper";


const AuthPage = () => {
    const [showPassword, setShowPassword] = useState<boolean>(false);
    const [password, setPassword] = useState<string>("");
    const [email, setEmail] = useState<string>("");
    const [loading, setLoading] = useState<boolean>(false);
    const [errors, setErrors] = useState<string[]>([]);

    const authStore = useAuthStore();

    const emailRef = createRef<any>();
    const passwordRef = createRef<any>();
    const t = useTranslation();
    const navigate = useNavigate();

    const userStore = useUserStore();

    const handleClickShowPassword = () => setShowPassword(prev => !prev);
    const handleChangePassword = (e: any) => setPassword(e.target.value);
    const handleMouseDownPassword = (e: any) => e.preventDefault();
    const handleChangeEmail = (e: any) => setEmail(e.target.value);

    const handleClickButtonLogIn = async () => {
        setLoading(true);
        const loginResponse = await ApiService.Login(email, password);
        authStore.setCredentials(email, password, loginResponse);
        if (loginResponse.success) {
            localStorage.setItem("token", loginResponse.data.accessToken);
            localStorage.setItem("refreshToken", loginResponse.data.refreshToken);
            userStore.login();
        } else {
            const needText = CheckNeedAdditionalAuth(loginResponse);
            if (needText) {
                navigate(needText);
                return;
            }
            
            setErrors(loginResponse.errors);
        }
        setLoading(false);
    }

    const [shrink, setShrink] = useState(false);
    const [shrinkPassword, setShrinkPassword] = useState(false);
    const [autoFill, setAutoFill] = useState(false);
    const animationStart = (e: any) => {
        switch (e.animationName) {
            case "mui-auto-fill":
                setShrink(true);
                setShrinkPassword(true);
                setAutoFill(true);
        }
    };

    useEffect(() => {
        emailRef.current.addEventListener('animationstart', animationStart);
        passwordRef.current.addEventListener('animationstart', animationStart);
    }, []);
    return (
        <Container>
            <Stack
                spacing={2}
                sx={{
                    maxWidth: 400,
                    margin: "auto",
                    marginTop: 5
                }}
                justifyContent={'center'}
            >
                <Typography variant="h4" align={'center'}>
                    {t("Log In Title")}
                </Typography>

                <TextField
                    disabled={loading}
                    fullWidth
                    id="email"
                    value={email}
                    onChange={handleChangeEmail}
                    inputProps={{inputMode: "email"}}
                    onKeyPress={e => {
                        if (e.code === "Enter") {
                            if (email === "" || password === "") return;
                            handleClickButtonLogIn();
                        }
                    }}
                    onBlur={() => email ? setShrink(true) : setShrink(false)}
                    onFocus={() => setShrink(true)}
                    InputLabelProps={{
                        shrink: shrink,
                    }}
                    ref={emailRef}

                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    disabled={true}
                                    edge="end"
                                    className={"changeColorAccountCircle"}
                                >
                                    <AccountCircle/>
                                </IconButton>
                            </InputAdornment>
                        ),
                    }
                    }
                    aria-describedby="outlined-email-helper-text"
                    label={t("Email")}
                    variant="outlined"
                />

                <TextField
                    disabled={loading}
                    fullWidth
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={handleChangePassword}
                    onKeyPress={e => {
                        if (e.code === "Enter") {
                            if (email === "" || password === "") return;
                            handleClickButtonLogIn();
                        }
                    }}
                    onBlur={() => password ? setShrinkPassword(true) : setShrinkPassword(false)}
                    onFocus={() => setShrinkPassword(true)}
                    InputLabelProps={{
                        shrink: shrinkPassword,
                    }}
                    ref={passwordRef}
                    InputProps={{
                        endAdornment: (
                            <InputAdornment position="end">
                                <IconButton
                                    aria-label="toggle password visibility"
                                    onClick={handleClickShowPassword}
                                    onMouseDown={handleMouseDownPassword}
                                    edge="end"
                                >
                                    {showPassword ? <VisibilityOff/> : <Visibility/>}
                                </IconButton>
                            </InputAdornment>
                        )
                    }}
                    label={t("Password")}
                    variant="outlined"
                />
                {errors?.map(e => <Alert severity="error">{e}</Alert>)}
                <LoadingButton disabled={(email === "" || password === "") && !autoFill} loading={loading}
                               size={"large"}
                               loadingPosition="start" startIcon={<></>} variant="contained"
                               onClick={handleClickButtonLogIn}>{loading ? `${t("Logging in")}...` : t("Log In")}</LoadingButton>

                <Typography variant="body2" align={"center"} style={{
                    fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen'," +
                        "    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue'," +
                        "    sans-serif"
                }}>
                    By logging into CFlow<sup style={{fontSize: "0.5rem"}}>TM</sup>, you agree to our
                    <a style={{marginLeft: 5, marginRight: 5}} href={'https://carolynleepllc.com/privacy-policy/'}
                       target={"_blank"} >Privacy Policy</a>
                    and our
                    <a style={{marginLeft: 5}}
                       href={'https://carolynleepllc.app.box.com/s/oeaplmmrlp7ak24qpxzt84j4x3hm6wyp'} target={"_blank"}>Terms
                        and Conditions</a>.
                </Typography>

                <NavLink to="/reset" style={{textAlign: "center"}}>
                    {t("Reset password")}
                </NavLink>
            </Stack>

        </Container>
    )
}

export default AuthPage;